import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/ui/Icon.jsx';
import { useBackDismiss } from '../../hooks.js';
import { MODULES, SKUPINE, moduleDone } from '../../interactive/modules.js';
import { KVIZ } from '../../interactive/kviz.js';
import * as api from '../../lib/api.js';
import { t } from '../../i18n/index.js';

/*
  Interaktivno učenje.

  Seznam vsebin; ob izbiri najprej razlaga, pod njo interaktivni del.
  Interaktivni del (three.js) se naloži šele ob odprtju vsebine in se ob
  zaprtju v celoti počisti — ne teče v ozadju in ne troši baterije.

  Opravljene stopnje in rezultati preverjanja gredo v bazo
  (ucenje_rezultati), da jih vidi inštruktor. Na napravi ostanejo tudi
  lokalno, zato vaja deluje brez povezave.
*/

export function Ucenje({ uid = null, ucenje = [], onChanged, readOnly = false }) {
  const [openId, setOpenId] = useState(null);
  const open = MODULES.find((m) => m.id === openId) ?? null;

  // Sistemski „nazaj" zapre vsebino in vrne na seznam.
  useBackDismiss(!!open, () => setOpenId(null));

  const show = (id) => {
    setOpenId(id);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // Zapis v bazo je tiho: če ni povezave, ostane zapis na napravi.
  const save = async (modul, kljuc, rezultat) => {
    if (readOnly || !uid) return;
    try {
      await api.saveUcenje(uid, modul, kljuc, rezultat);
      onChanged?.();
    } catch {
      /* ni povezave ali predogled */
    }
  };

  if (open) return <Vsebina m={open} rows={ucenje} onSave={save} readOnly={readOnly} onBack={() => setOpenId(null)} />;

  return (
    <div className="stack stack--loose">
      <section className="stack stack--tight">
        <h2 className="t-xl">{t('Interaktivno učenje')}</h2>
        <p className="t-md t-dim">
          {t('Vsaka vsebina ima najprej razlago, nato jo preizkusiš sam. Ne nadomesti terena, pripravi te nanj.')}
        </p>
      </section>

      {SKUPINE.map((g) => {
        const list = MODULES.filter((m) => g.ids.includes(m.id));
        if (!list.length) return null;
        const opravljenih = list.filter((m) => {
          const { done, of } = moduleDone(m, ucenje);
          return of > 0 && done.length === of;
        }).length;
        return (
          <section key={g.key} className="stack stack--tight" aria-labelledby={`skupina-${g.key}`}>
            <div className="row">
              <h3 id={`skupina-${g.key}`} className="eyebrow spacer">
                {t(g.label)}
              </h3>
              <span className="t-xs t-dimmer num">
                {opravljenih}/{list.length}
              </span>
            </div>
            <ul className="stack" style={{ listStyle: 'none' }}>
              {list.map((m) => {
                const { done, of } = moduleDone(m, ucenje);
                const kv = ucenje.find((r) => r.modul === m.id && r.kljuc === 'kviz');
                const vse = of > 0 && done.length === of;
                return (
                  <li key={m.id}>
                    <button
                      className="card card--pad row row--top"
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        cursor: 'pointer',
                        gap: 'var(--s-4)',
                        ...(m.warn ? { borderColor: 'var(--bad-border)' } : vse ? { borderColor: 'var(--ok-border)' } : null),
                      }}
                      onClick={() => show(m.id)}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: 44,
                          height: 44,
                          flexShrink: 0,
                          borderRadius: 12,
                          display: 'grid',
                          placeItems: 'center',
                          background: m.warn ? 'var(--bad-wash)' : vse ? 'var(--ok-wash)' : 'var(--brand-wash)',
                          color: m.warn ? 'var(--bad-text)' : vse ? 'var(--ok-text)' : 'var(--brand-ink)',
                        }}
                      >
                        <Icon name={vse ? 'check' : m.icon} size={22} />
                      </span>
                      <span className="spacer stack stack--tight">
                        <span className="t-sm t-dim">{t(m.vaje)}</span>
                        <span className="t-lg t-bold" style={m.warn ? { color: 'var(--bad-text)' } : undefined}>
                          {t(m.title)}
                        </span>
                        <span className="t-md t-dim">{t(m.short)}</span>
                        <span className="t-sm t-dimmer">
                          {t(m.meta)}
                          {done.length ? (of > 1 ? ` · ${t('opravljeno {a} od {b}', { a: done.length, b: of })}` : ` · ${t('opravljeno')}`) : ''}
                          {kv ? ` · ${t('preverjanje')} ${kv.pravilnih}/${kv.vseh}` : ''}
                        </span>
                      </span>
                      <Icon name="right" size={18} className="t-dimmer" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function Vsebina({ m, rows, onSave, readOnly, onBack }) {
  return (
    <div className="stack stack--loose">
      <button className="btn btn--ghost btn--sm" style={{ alignSelf: 'flex-start' }} onClick={onBack}>
        <Icon name="left" size={16} /> {t('Vse vsebine')}
      </button>

      <section className="stack stack--tight">
        <span className="t-sm t-dim">{t(m.vaje)}</span>
        <h2 className="t-xl" style={m.warn ? { color: 'var(--bad-text)' } : undefined}>
          {t(m.title)}
        </h2>
        <p className="t-md">{m.intro}</p>
      </section>

      {m.note && (
        <div
          className="card card--pad"
          role="note"
          style={{ background: 'var(--bad-wash)', borderColor: 'var(--bad-border)' }}
        >
          <p className="t-md t-bold" style={{ color: 'var(--bad-text)' }}>
            {m.note}
          </p>
        </div>
      )}

      {m.warn && (
        <div
          className="card card--pad"
          role="note"
          style={{ background: 'var(--bad-wash)', borderColor: 'var(--bad-border)' }}
        >
          <p className="t-md t-bold" style={{ color: 'var(--bad-text)' }}>
            {t('Samo prikaz. Namerno tega nikoli ne narediš.')}
          </p>
        </div>
      )}

      <ol className="stack" style={{ listStyle: 'none' }}>
        {m.steps.map(([h, p], i) => (
          <li key={h} className="row row--top" style={{ gap: 'var(--s-3)' }}>
            <span className="num t-bold" style={{ color: 'var(--brand-ink)', minWidth: 18 }}>
              {i + 1}
            </span>
            <span className="spacer">
              <span className="t-md t-bold">{h}</span>
              <p className="t-md t-dim" style={{ marginTop: 2 }}>
                {p}
              </p>
            </span>
          </li>
        ))}
      </ol>

      <section className="stack stack--tight">
        <h3 className="t-lg">{t('Preizkusi')}</h3>
        <p className="t-sm t-dim">{m.how}</p>
      </section>

      <Interaktivno m={m} onDone={(k) => onSave(m.id, k)} />

      <p className="t-sm t-dimmer">
        {t('Simulacija je poenostavljena. Pokaže pravo zaporedje in občutek, terena ne nadomesti.')}
      </p>

      {KVIZ[m.id] && (
        <Kviz
          key={m.id}
          m={m}
          best={rows.find((r) => r.modul === m.id && r.kljuc === 'kviz') ?? null}
          readOnly={readOnly}
          onSave={(rez) => onSave(m.id, 'kviz', rez)}
        />
      )}
    </div>
  );
}

/* Naloži interaktivni del in ga ob zaprtju počisti. */
function Interaktivno({ m, onDone }) {
  const ref = useRef(null);
  const [state, setState] = useState('loading');
  // Prizor se ne sme znova naložiti, ko se spremeni povratni klic.
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    let destroy = null;
    let cancelled = false;
    setState('loading');
    m.load()
      .then((mount) => {
        if (cancelled || !ref.current) return;
        destroy = mount(ref.current, { onDone: (k) => done.current?.(k) });
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });
    return () => {
      cancelled = true;
      if (destroy) destroy();
    };
  }, [m]);

  return (
    <div>
      {state === 'loading' && (
        <div className="card card--pad t-md t-dim" role="status">
          {t('Nalagam 3D prikaz …')}
        </div>
      )}
      {state === 'error' && (
        <div className="card card--pad t-md" role="alert">
          {t('3D prikaza ni bilo mogoče naložiti. Preveri povezavo in poskusi znova.')}
        </div>
      )}
      <div ref={ref} data-testid="interaktivno" />
    </div>
  );
}

/*
  Kratko preverjanje znanja po vsebini. Vprašanja in razlage so v
  interactive/kviz.js. Shrani se najboljši rezultat.
*/
function Kviz({ m, best, readOnly, onSave }) {
  const qs = KVIZ[m.id];
  const [ans, setAns] = useState({});
  const [checked, setChecked] = useState(false);
  const all = qs.every((_, i) => ans[i] != null);
  const score = qs.filter((q, i) => ans[i] === q.ok).length;

  const check = () => {
    setChecked(true);
    if (!best || score > (best.pravilnih ?? 0)) onSave({ pravilnih: score, vseh: qs.length });
  };
  const again = () => {
    setAns({});
    setChecked(false);
  };

  return (
    <section className="stack" aria-labelledby={`kviz-${m.id}`}>
      <div className="stack stack--tight">
        <h3 className="t-lg" id={`kviz-${m.id}`}>
          {t('Preveri znanje')}
        </h3>
        <p className="t-sm t-dim">
          {t('{n} kratka vprašanja o tej vsebini.', { n: qs.length })}
          {best ? ` ${t('Najboljši rezultat: {a} od {b}.', { a: best.pravilnih, b: best.vseh })}` : ''}
        </p>
      </div>

      <ol className="stack" style={{ listStyle: 'none' }}>
        {qs.map((q, i) => (
          <li key={q.q} className="card card--pad">
            <fieldset style={{ border: 0 }}>
              <legend className="t-md t-bold" style={{ marginBottom: 'var(--s-3)' }}>
                <span className="t-brand num">{i + 1}.</span> {q.q}
              </legend>
              <div className="stack stack--tight">
                {q.a.map((opt, oi) => {
                  const chosen = ans[i] === oi;
                  const cls = checked
                    ? oi === q.ok
                      ? ' choice--correct'
                      : chosen
                        ? ' choice--wrong'
                        : ''
                    : chosen
                      ? ' choice--on'
                      : '';
                  return (
                    <label key={oi} className={`choice${cls}`}>
                      <input
                        type="radio"
                        name={`kviz-${m.id}-${i}`}
                        className="checkbox"
                        style={{ borderRadius: '50%' }}
                        checked={chosen}
                        disabled={checked}
                        onChange={() => setAns((a) => ({ ...a, [i]: oi }))}
                      />
                      <span className="spacer t-md">{opt}</span>
                    </label>
                  );
                })}
              </div>
              {checked && (
                <p className={`t-sm ${ans[i] === q.ok ? 't-ok' : 't-bad'}`} style={{ marginTop: 'var(--s-2)' }}>
                  {ans[i] === q.ok ? t('Pravilno. ') : t('Ni pravilno. ')}
                  <span className="t-dim">{q.why}</span>
                </p>
              )}
            </fieldset>
          </li>
        ))}
      </ol>

      {checked ? (
        <div className={`card card--pad ${score === qs.length ? 'card--ok' : 'card--brand'} row row--wrap`} style={{ gap: 'var(--s-3)' }}>
          <span className="spacer t-md t-bold" role="status">
            {score === qs.length ? t('Vse pravilno.') : t('Pravilnih {a} od {b}.', { a: score, b: qs.length })}
            {readOnly ? '' : t(' Rezultat je shranjen za inštruktorja.')}
          </span>
          <button className="btn btn--secondary" onClick={again}>
            {t('Znova')}
          </button>
        </div>
      ) : (
        <button className="btn btn--primary" style={{ alignSelf: 'flex-start' }} disabled={!all} onClick={check}>
          {t('Preveri odgovore')}
        </button>
      )}
    </section>
  );
}
